// TransformationLayer/vanilaIndex.js
import React, { useState, useEffect, useRef } from 'react';
import TransformationPlayer from './TransformationPlayerVanilla';

// ============================================================
// 1. DEFINE YOUR TEMPLATE
// ============================================================
const template = `Room:
  id - {{ response_data.room.id }}
  room_number - {{ response_data.room.room_number }}
  room_type - {{ response_data.room.room_type }}
  room_img - {{ response_data.room.room_img }}
Bookings:
{% for booking in response_data.bookings %}
    id - {{ booking.id }}
    user_id - {{ booking.user_id }}
    room_img - {{ booking.room_img }}
    check_in - {{ booking.check_in }}
{% endfor %}
A Regular Btn
Button Group1`;

// ============================================================
// 2. DEFINE YOUR SAMPLE RESPONSE
// ============================================================
const sampleResponse = {
  "room": {
    "id": 1,
    "room_number": "101",
    "room_type": "Standard",
    "room_img": "https://images.pexels.com/photos/26934147/pexels-photo-26934147.jpeg"
  },

  "bookings": [
    {
      "id": 13,
      "user_id": 3,
      "room_img": "https://images.pexels.com/photos/34376514/pexels-photo-34376514.jpeg",
      "check_in": "2026-08-05T18:30:00.000Z"

    },
    {
      "id": 11,
      "user_id": 3,
      "room_img": "https://images.pexels.com/photos/11363655/pexels-photo-11363655.jpeg",
      "check_in": "2026-07-31T18:30:00.000Z"

    }
  ]
};

// ============================================================
// 3. DEFINE YOUR TRANSFORMATION STEPS
// Using selectors array format with loopSelector
// ============================================================
const transformationSteps = [
		{
			"_templateRowIndex": 12,
			"buttonText": "A Regular Btn",
			"id": "btn_1788627869108_yr8rer",
			"operation": "isButton",
			"selectors": [
				{
					"end": "div:nth-child(1) > div:nth-child(1) > div:nth-child(8)",
					"start": "div:nth-child(1) > div:nth-child(1) > div:nth-child(8)"
				}
			],
			"timestamp": "2026-09-05T17:04:29.108Z",
			"type": "button"
		},
		{
			"_templateRowIndex": 13,
			"buttons": [
				"Button 1",
				"Button 2"
			],
			"groupName": "Button Group1",
			"id": "btngrp_1788627897732_tfqsek",
			"operation": "isButtonGroup",
			"selectors": [
				{
					"end": "div:nth-child(1) > div:nth-child(1) > div:nth-child(9)",
					"start": "div:nth-child(1) > div:nth-child(1) > div:nth-child(9)"
				}
			],
			"timestamp": "2026-09-05T17:04:57.732Z",
			"type": "button-group"
		}
	];

const TransformationPlayerTest = () => {
  const containerRef = useRef(null);
  const playerRef = useRef(null);
  const [results, setResults] = useState([]);
  const [status, setStatus] = useState('idle');
  const [currentStepIndex, setCurrentStepIndex] = useState(0);
  const [error, setError] = useState(null);
  const [caseType, setCaseType] = useState('unknown');
  const [caseDescription, setCaseDescription] = useState('');

  useEffect(() => {
    if (playerRef.current || !containerRef.current) return;

    try {
      console.log('[TransformationPlayerTest] 🚀 Initializing player...');
      const player = new TransformationPlayer({
        template: template,
        sampleResponse: sampleResponse,
        transformationSteps: transformationSteps,
        targetElement: containerRef.current,
        onStepComplete: (result, index) => {
          console.log(`[TransformationPlayerTest] ✅ Step ${index + 1} completed:`, result);
          setResults(prev => [...prev, {
            stepNumber: index + 1,
            type: result.step?.type || 'unknown',
            operation: result.step?.operation || 'unknown',
            fields: result.step?.fields || [],
            success: true,
            timestamp: result.timestamp
          }]);
          setCurrentStepIndex(index + 1);
        },
        onError: (errorMsg) => {
          console.error('[TransformationPlayerTest] ❌ Player error:', errorMsg);
          setError(errorMsg);
          setStatus('error');
        }
      });

      setCaseType(player.caseType || 'unknown');
      setCaseDescription(player.caseDescription || '');
      playerRef.current = player;
      setStatus('ready');
      console.log('[TransformationPlayerTest] ✅ Player initialized');
    } catch (err) {
      console.error('[TransformationPlayerTest] ❌ Error initializing player:', err);
      setError(err.message);
      setStatus('error');
    }

    return () => {
      if (playerRef.current) {
        console.log('[TransformationPlayerTest] 🧹 Cleaning up player');
        playerRef.current.destroy();
        playerRef.current = null;
      }
    };
  }, []);

  const handleReset = () => {
    if (playerRef.current) {
      playerRef.current.reset();
      setResults([]);
      setCurrentStepIndex(0);
      setStatus('idle');
      setError(null);
      setCaseType(playerRef.current.caseType || 'unknown');
      setCaseDescription(playerRef.current.caseDescription || '');
    }
  };

  const handlePlayAll = async () => {
    if (playerRef.current) {
      setStatus('playing');
      setError(null);
      try {
        await playerRef.current.playAll();
        setStatus('idle');
        setCaseType(playerRef.current.caseType || 'unknown');
        setCaseDescription(playerRef.current.caseDescription || '');
      } catch (err) {
        setError(err.message);
        setStatus('error');
      }
    }
  };

  const handleNextStep = () => {
    if (playerRef.current) {
      setError(null);
      try {
        playerRef.current.playNext();
        setTimeout(() => {
          setStatus(playerRef.current?.isPlaying ? 'playing' : 'idle');
          setCaseType(playerRef.current?.caseType || 'unknown');
          setCaseDescription(playerRef.current?.caseDescription || '');
        }, 100);
      } catch (err) {
        setError(err.message);
        setStatus('error');
      }
    }
  };

  const handlePrevStep = () => {
    if (playerRef.current && currentStepIndex > 0) {
      playerRef.current.reset();
      setResults([]);
      setCurrentStepIndex(0);
      setError(null);

      const stepsToReplay = currentStepIndex - 1;
      for (let i = 0; i < stepsToReplay; i++) {
        try { playerRef.current.playNext(); } catch (err) { setError(err.message); break; }
      }

      const newResults = [];
      for (let i = 0; i < stepsToReplay; i++) {
        const step = transformationSteps[i];
        newResults.push({
          stepNumber: i + 1,
          type: step.type || 'unknown',
          operation: step.operation || 'unknown',
          fields: step.fields || [],
          success: true
        });
      }
      setResults(newResults);
      setCurrentStepIndex(stepsToReplay);
      setCaseType(playerRef.current?.caseType || 'unknown');
      setCaseDescription(playerRef.current?.caseDescription || '');
    }
  };

  // Show first selector info
  const firstStep = transformationSteps[0];
  const firstSelector = firstStep?.selectors?.[0] || null;

  return (
    <div style={{ padding: '20px', maxWidth: '1400px', margin: '0 auto', fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif' }}>
      <h1 style={{ fontSize: '24px', fontWeight: '700', color: '#2d3748', marginBottom: '4px' }}>🧪 Transformation Player Test</h1>
      <p style={{ color: '#718096', marginBottom: '20px', fontSize: '14px' }}>Testing with selectors array format (loopSelector + relative paths)</p>

      <div style={{ padding: '8px 16px', backgroundColor: caseType === 'case3' ? '#fefcbf' : '#ebf8ff', border: `1px solid ${caseType === 'case3' ? '#f6ad55' : '#bee3f8'}`, borderRadius: '8px', marginBottom: '16px', fontSize: '13px', display: 'flex', alignItems: 'center', gap: '12px', flexWrap: 'wrap' }}>
        <span style={{ fontWeight: '600' }}>📊 Case: <span style={{ color: caseType === 'case3' ? '#744210' : '#2b6cb0', fontWeight: '700' }}>{caseType.toUpperCase()}</span></span>
        <span style={{ color: '#4a5568' }}>{caseDescription || 'Unknown case type'}</span>
        {caseType === 'case3' && <span style={{ padding: '2px 10px', backgroundColor: '#f6ad55', color: '#744210', borderRadius: '12px', fontSize: '11px', fontWeight: '600' }}>⚡ Multiple Screens</span>}
        {caseType === 'case2' && <span style={{ padding: '2px 10px', backgroundColor: '#bee3f8', color: '#2b6cb0', borderRadius: '12px', fontSize: '11px', fontWeight: '600' }}>📄 Single Screen</span>}
        {caseType === 'case1' && <span style={{ padding: '2px 10px', backgroundColor: '#48bb78', color: 'white', borderRadius: '12px', fontSize: '11px', fontWeight: '600' }}>📚 Root Array</span>}
      </div>

      {/* Display selector info */}
      {firstSelector && (
        <div style={{ padding: '12px 16px', backgroundColor: '#ebf8ff', border: '1px solid #bee3f8', borderRadius: '8px', marginBottom: '16px', fontSize: '13px' }}>
          <strong>🔍 Current Selector (Loop-aware):</strong>
          <pre style={{ margin: '8px 0 0 0', fontSize: '12px', fontFamily: 'monospace', whiteSpace: 'pre-wrap', wordBreak: 'break-all', backgroundColor: 'white', padding: '8px', borderRadius: '4px', maxHeight: '150px', overflow: 'auto' }}>
            {JSON.stringify(firstSelector, null, 2)}
          </pre>
          <div style={{ marginTop: '8px', fontSize: '12px', color: '#4a5568' }}>
            <span style={{ fontWeight: '600' }}>loopSelector:</span> {firstSelector.loopSelector || '(none)'}<br />
            <span style={{ fontWeight: '600' }}>relative path:</span> {firstSelector.start?.elementPath || '(none)'}
          </div>
        </div>
      )}

      {error && <div style={{ padding: '12px 16px', backgroundColor: '#fed7d7', border: '1px solid #feb2b2', borderRadius: '8px', color: '#9b2c2c', marginBottom: '16px', fontSize: '14px' }}><strong>❌ Error:</strong> {error}</div>}

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: '12px', marginBottom: '20px' }}>
        <div style={{ padding: '12px 16px', backgroundColor: '#f7fafc', borderRadius: '8px', border: '1px solid #e2e8f0' }}>
          <div style={{ fontSize: '11px', color: '#718096', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Total Steps</div>
          <div style={{ fontSize: '20px', fontWeight: '700', color: '#2d3748' }}>{transformationSteps.length}</div>
        </div>
        <div style={{ padding: '12px 16px', backgroundColor: '#f7fafc', borderRadius: '8px', border: '1px solid #e2e8f0' }}>
          <div style={{ fontSize: '11px', color: '#718096', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Current Step</div>
          <div style={{ fontSize: '20px', fontWeight: '700', color: '#2d3748' }}>{currentStepIndex > 0 ? `${currentStepIndex}/${transformationSteps.length}` : '⏸ Waiting'}</div>
        </div>
        <div style={{ padding: '12px 16px', backgroundColor: '#f7fafc', borderRadius: '8px', border: '1px solid #e2e8f0' }}>
          <div style={{ fontSize: '11px', color: '#718096', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Status</div>
          <div style={{ fontSize: '16px', fontWeight: '600', color: status === 'playing' ? '#2b6cb0' : status === 'error' ? '#e53e3e' : '#48bb78' }}>
            {status === 'playing' ? '⏳ Playing' : status === 'error' ? '❌ Error' : '✅ Ready'}
          </div>
        </div>
        <div style={{ padding: '12px 16px', backgroundColor: '#f7fafc', borderRadius: '8px', border: '1px solid #e2e8f0' }}>
          <div style={{ fontSize: '11px', color: '#718096', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Results</div>
          <div style={{ fontSize: '20px', fontWeight: '700', color: '#2d3748' }}>{results.length}</div>
        </div>
      </div>

      <div style={{ display: 'flex', gap: '10px', marginBottom: '16px', flexWrap: 'wrap', alignItems: 'center', padding: '12px 16px', backgroundColor: '#f7fafc', borderRadius: '8px', border: '1px solid #e2e8f0' }}>
        <button onClick={handlePlayAll} disabled={status === 'playing'} style={{ padding: '8px 20px', backgroundColor: status === 'playing' ? '#a0aec0' : '#48bb78', color: 'white', border: 'none', borderRadius: '6px', cursor: status === 'playing' ? 'not-allowed' : 'pointer', fontSize: '14px', fontWeight: '600', transition: 'all 0.2s' }}>
          {status === 'playing' ? '⏳ Playing...' : '▶ Play All'}
        </button>
        <button onClick={handleNextStep} disabled={status === 'playing' || currentStepIndex >= transformationSteps.length} style={{ padding: '8px 20px', backgroundColor: (status === 'playing' || currentStepIndex >= transformationSteps.length) ? '#a0aec0' : '#4299e1', color: 'white', border: 'none', borderRadius: '6px', cursor: (status === 'playing' || currentStepIndex >= transformationSteps.length) ? 'not-allowed' : 'pointer', fontSize: '14px', fontWeight: '600', transition: 'all 0.2s' }}>
          ⏭ Next Step
        </button>
        <button onClick={handlePrevStep} disabled={status === 'playing' || currentStepIndex <= 0} style={{ padding: '8px 20px', backgroundColor: (status === 'playing' || currentStepIndex <= 0) ? '#a0aec0' : '#ed8936', color: 'white', border: 'none', borderRadius: '6px', cursor: (status === 'playing' || currentStepIndex <= 0) ? 'not-allowed' : 'pointer', fontSize: '14px', fontWeight: '600', transition: 'all 0.2s' }}>
          ⏮ Prev Step
        </button>
        <button onClick={handleReset} disabled={status === 'playing'} style={{ padding: '8px 20px', backgroundColor: status === 'playing' ? '#a0aec0' : '#fc8181', color: 'white', border: 'none', borderRadius: '6px', cursor: status === 'playing' ? 'not-allowed' : 'pointer', fontSize: '14px', fontWeight: '600', transition: 'all 0.2s' }}>
          🔄 Reset
        </button>
        <span style={{ marginLeft: 'auto', fontSize: '13px', color: '#718096', display: 'flex', alignItems: 'center', gap: '8px' }}>
          <span>Steps: {transformationSteps.length}</span>
          <span>|</span>
          <span>Status: <strong style={{ color: status === 'playing' ? '#2b6cb0' : status === 'error' ? '#e53e3e' : '#4a5568' }}>
            {status === 'playing' ? '▶ Playing' : status === 'error' ? '❌ Error' : '⏸ Idle'}
          </strong></span>
        </span>
      </div>

      <div ref={containerRef} id="player-container" className="tp-container" style={{ background: '#f7fafc', borderRadius: '8px', border: '1px solid #e2e8f0', padding: '16px', minHeight: '500px', overflow: 'auto' }} />

      {results.length > 0 && (
        <div style={{ marginTop: '20px', padding: '16px', backgroundColor: '#f0fff4', border: '1px solid #48bb78', borderRadius: '8px' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
            <h4 style={{ margin: 0, color: '#22543d' }}>📊 Transformation Results</h4>
            <span style={{ fontSize: '12px', color: '#48bb78' }}>{results.length} step{results.length > 1 ? 's' : ''} applied</span>
          </div>
          <div style={{ maxHeight: '200px', overflow: 'auto', fontSize: '12px', fontFamily: 'monospace', backgroundColor: 'white', padding: '12px', borderRadius: '4px', border: '1px solid #e2e8f0' }}>
            {results.map((result, index) => (
              <div key={index} style={{ display: 'flex', alignItems: 'center', gap: '12px', padding: '4px 8px', borderBottom: '1px solid #f0f0f0' }}>
                <span style={{ fontWeight: '600', color: '#2d3748', minWidth: '60px' }}>Step {result.stepNumber}:</span>
                <span style={{ padding: '1px 8px', borderRadius: '10px', fontSize: '10px', fontWeight: '600', textTransform: 'uppercase', backgroundColor: result.type === 'structural' || result.type === 'multiple' ? '#ebf8ff' : '#fefcbf', color: result.type === 'structural' || result.type === 'multiple' ? '#2b6cb0' : '#744210' }}>
                  {result.type || 'unknown'}
                </span>
                <span style={{ fontWeight: '500', color: '#2d3748' }}>{result.operation || 'unknown'}</span>
                {result.fields && result.fields.length > 0 && <span style={{ color: '#718096', fontSize: '11px' }}>fields: {result.fields.join(', ')}</span>}
                <span style={{ marginLeft: 'auto', color: '#48bb78' }}>✅</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default TransformationPlayerTest;